import { styled } from '@mui/material/styles';
import { Box, Tabs, Typography, Button } from '@mui/material';
import { HEADER_HEIGHT } from '../../../constants/constants';

// 페이지 탭바(StyledTabs minHeight 44px + 여유) 높이를 더해, 사이드바 sticky가
// 글로벌 헤더 + 페이지 탭바 둘 다의 아래에서 자리 잡도록 한다.
const PAGE_TABS_HEIGHT = 52;

// ============================================
// Page Layout
// 카드 디자인은 PassCard / CourseTicketCard / TrialTicketCard 자체 보유.
// 이 파일은 그 위 레이아웃(탭 + sub-section 그리드)만 책임.
// ============================================
export const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  // 좌우 margin 0으로 상단 회원 헤더 카드(이름/전화번호)와 좌측 시작점을 맞춘다.
  margin: '16px 0',
});

// ============================================
// Top Tabs — 사용 중 / 만료됨 전환
// 한 번에 한 zone만 노출해 vertical scroll을 절반으로 줄인다.
// 탭 라벨에 건수를 넣어 "한눈에 요약" 효과 유지.
// ============================================
export const TabsBar = styled(Tabs)({
  width: '100%',
  borderBottom: '1px solid #e5e5e5',
  minHeight: '48px',
  '& .MuiTabs-indicator': {
    backgroundColor: '#4AB3BC',
    height: '3px',
  },
  '& .MuiTab-root': {
    fontSize: '15px',
    fontWeight: 500,
    textTransform: 'none',
    color: '#737373',
    minHeight: '48px',
    padding: '12px 24px',
    letterSpacing: '-0.15px',
  },
  '& .MuiTab-root.Mui-selected': {
    color: '#4AB3BC',
    fontWeight: 600,
  },
});

// ============================================
// Layout Shell — 좌측 카테고리 사이드바 + 우측 본문
// 본문은 사이드바가 카테고리 필터 역할을 하므로 단순 vertical stack.
// ============================================
export const LayoutShell = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '24px',
  width: '100%',
});

// ============================================
// 사이드 필터 (상태 · 카테고리) — 시안 3c "그룹 카드 분리형"
// 상태/카테고리를 각각 별도 카드로 분리. 회색 헤더 바 + 상태 앞 세로 컬러 바,
// 선택 행은 청록 tint(#E0F4F5) + 진한 청록 텍스트. 로직/데이터는 기존 그대로, 렌더링만 교체.
// ============================================

// 사이드바 외곽 — 상태 카드 + 카테고리 카드를 세로로.
export const Sidebar = styled(Box)({
  flex: '0 0 260px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  position: 'sticky',
  // 글로벌 AppBar(HEADER_HEIGHT) + 페이지 탭바 아래에 고정.
  top: `${HEADER_HEIGHT + PAGE_TABS_HEIGHT}px`,
});

// 개별 필터 카드 (상태 / 카테고리)
export const FilterCard = styled(Box)({
  background: '#fff',
  border: '1px solid #E2E6EA',
  borderRadius: '12px',
  overflow: 'hidden',
});

// 카드 헤더 바 — 섹션 제목(+ 카테고리는 우측 요약)
export const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  background: '#F7F9FA',
  borderBottom: '1px solid #EEF0F2',
  padding: '10px 14px',
  fontSize: '13px',
  fontWeight: 800,
  color: '#26313C',
});

// 카테고리 헤더 우측 "전체 N" 요약 겸 전체 선택. 선택 시 청록 강조.
export const CardHeaderSummary = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '12px',
  fontWeight: isActive ? 800 : 500,
  color: isActive ? '#00838F' : '#98A1AB',
  cursor: 'pointer',
}));

export const CardBody = styled(Box)({
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

// 필터 행 — depth 0(상태/부모), depth 1(카테고리 하위, 들여쓰기)
export const SidebarItem = styled(Box, {
  shouldForwardProp: prop => !['isActive', 'depth', 'dimmed'].includes(prop),
})(({ isActive, depth = 0, dimmed }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '8px',
  padding: depth === 0 ? '10px 12px' : '9px 12px 9px 30px',
  cursor: 'pointer',
  opacity: dimmed ? 0.45 : 1,
  background: isActive ? '#E0F4F5' : 'transparent',
  transition: 'background 0.12s ease',
  '&:hover': {
    background: isActive ? '#E0F4F5' : '#F2F7F8',
  },
}));

// 상태 행 앞 세로 컬러 바 (4×16)
export const SidebarBar = styled('span', {
  shouldForwardProp: prop => prop !== 'color',
})(({ color }) => ({
  width: '4px',
  height: '16px',
  borderRadius: '2px',
  backgroundColor: color,
  flexShrink: 0,
}));

// 라벨 텍스트(좌측). 세로 바/캐럿을 앞에 포함하며 길어지면 ellipsis.
export const SidebarItemLabel = styled(Box, {
  shouldForwardProp: prop => !['isActive', 'depth', 'isParent'].includes(prop),
})(({ isActive, depth = 0, isParent }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: isParent ? '7px' : '8px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: depth === 0 ? '14px' : '13.5px',
  fontWeight: isActive ? 800 : isParent ? 700 : 500,
  color: isActive ? '#00838F' : isParent ? '#26313C' : '#3C4650',
}));

// 카운트 — pill 없이 우측 숫자. 선택 시 진한 청록.
export const SidebarBadge = styled(Box, {
  shouldForwardProp: prop => !['isActive', 'depth'].includes(prop),
})(({ isActive, depth = 0 }) => ({
  flexShrink: 0,
  fontSize: depth === 0 ? '14px' : '13.5px',
  fontWeight: isActive ? 800 : 700,
  color: isActive ? '#00838F' : '#5B6470',
}));

// 부모(그룹) 행 접힘 캐럿 (▾/▸)
export const SidebarCaret = styled('span')({
  display: 'inline-block',
  width: '10px',
  fontSize: '10px',
  color: '#98A1AB',
  flexShrink: 0,
  textAlign: 'center',
});

export const MainContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const BulkStopButton = styled(Button)({
  height: '36px',
  padding: '8px 16px',
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 600,
  '&:hover': {
    background: '#E0F4F5',
    border: '1px solid #4AB3BC',
  },
});

// ============================================
// Zone — 본문 안 큰 묶음. 사이드바가 카테고리 분리 역할이라 단순 vertical stack으로 회귀.
// ============================================
export const Zone = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
});

// ============================================
// Section — 회원권 / 일반상품 같은 큰 카테고리 단위
// ============================================
export const Section = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

export const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  paddingBottom: '8px',
  borderBottom: '1px solid #ebebeb',
});

export const SectionTitle = styled(Typography, {
  shouldForwardProp: prop => prop !== 'muted',
})(({ muted }) => ({
  fontSize: '17px',
  fontWeight: 700,
  color: muted ? '#737373' : '#262626',
  letterSpacing: '-0.17px',
}));

export const SectionTotal = styled(Typography, {
  shouldForwardProp: prop => prop !== 'muted',
})(({ muted }) => ({
  fontSize: '13px',
  fontWeight: 500,
  color: muted ? '#a1a1a1' : '#4AB3BC',
}));

// 섹션 헤더 우측 끝 액션 슬롯(예: 상품일괄중지 버튼). SectionHeader가 baseline 정렬이라
// 버튼은 별도로 세로 중앙 정렬해 텍스트와 높이를 맞춘다.
export const SectionHeaderAction = styled(Box)({
  marginLeft: 'auto',
  marginRight: '24px',
  alignSelf: 'center',
  // 헤더 행 여백 때문에 버튼이 살짝 낮아 보여 약간 위로 올림.
  transform: 'translateY(-3px)',
});

// 사이드바 leaf(단일 카테고리) 화면에서는 SectionHeader가 없어 버튼만 우측 정렬로 배치.
export const LeafActionRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
});

// ============================================
// Subsection — 이용권 / 일일권 / 수강권 / 라커 / 일반상품
// ============================================
export const Subsection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const SubsectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const SubsectionTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  letterSpacing: '-0.15px',
});

export const CountBadge = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#4AB3BC',
});

export const CountBadgeGray = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#a1a1a1',
});

// ============================================
// Card Grid — Card 자체 디자인은 변경하지 않으므로 wrap/gap만 담당.
// ============================================
export const CardGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
});

// ============================================
// Empty State — 항목이 0개일 때 분명히 표시.
// 2-컬럼 레이아웃에서 폭이 좁아질 수 있어 패딩을 줄여 컴팩트하게.
// ============================================
export const EmptyState = styled(Box)({
  padding: '14px 16px',
  textAlign: 'center',
  color: '#a1a1a1',
  fontSize: '13px',
  background: '#fafbfc',
  border: '1px dashed #e5e5e5',
  borderRadius: '8px',
});

// ============================================
// 라커 자리 묶음 토글 — 같은 자리의 연장 카드들을 접었다 편다.
// ============================================
export const SpotToggleRow = styled(Button)({
  justifyContent: 'flex-start',
  padding: '6px 4px',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 600,
  '&:hover': {
    background: '#E0F4F5',
  },
});
